import React, { useMemo, useRef, useState } from "react";
import { Draggable } from "react-beautiful-dnd";

import { Box, Typography } from "@mui/material";
import { Tooltip as MuiTooltip } from "@mui/material";
import { CardSizeProvider } from "components/Report-generator/context/CardSizeContext";
import { minWidthPercent, PRESET_WIDTHS, visibleColumnCount } from "components/Report-generator/utils/pageMetrics";
import type {
  AICardConfig,
  BenchmarkComparisonCardData,
  CanvasCard,
  CardConfig,
  CardType,
  CashFlowTimelineCardData,
  Chart,
  DataFreshnessCardData,
  DataTableCardData,
  ExposureCardData,
  KPICardData,
  MacroBriefCardData,
  PerformanceAttributionCardData,
  ReportOrientation,
  SizePreset,
  SummaryCardData,
  TableCardConfig,
  UnfundedCardData
} from "store/types";

import { AICard } from "./cards/AICard";
import { BenchmarkComparisonCard } from "./cards/BenchmarkComparisonCard";
import { CashFlowTimelineCard } from "./cards/CashFlowTimelineCard";
import { ChartCard } from "./cards/ChartCard";
import { DataFreshnessCard } from "./cards/DataFreshnessCard";
import { DataTableCard } from "./cards/DataTableCard";
import { ExposureBreakdownCard } from "./cards/ExposureBreakdownCard";
import { KpiCard } from "./cards/KpiCard";
import { MacroBriefCard } from "./cards/MacroBriefCard";
import { PerformanceAttributionCard } from "./cards/PerformanceAttributionCard";
import { SummaryCard } from "./cards/SummaryCard";
import { getMinWidths } from "./cards/tableColumns";
import { TextCard } from "./cards/TextCard";
import { UnfundedCommitmentsCard } from "./cards/UnfundedCommitmentsCard";

const CARD_LABELS: Record<CardType, string> = {
  kpi: "KPI Card",
  summary: "Summary Card",
  chart: "Chart",
  table: "Data Table",
  text: "Text Card",
  ai_card: "AI Card",
  exposure: "Exposure",
  cashflow: "Cash Flow",
  unfunded: "Unfunded",
  performance: "Performance",
  benchmark: "Benchmark",
  dataFreshness: "Freshness",
  macroBrief: "Macro Brief"
};

interface Props {
  card: CanvasCard;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onUpdateConfig: (config: CardConfig) => void;
  onWidthChange: (widthPercent: number, preset: SizePreset | null) => void;
  onHeightChange: (heightPx: number | undefined) => void;
  onTopOffsetChange: (topOffsetPx: number | undefined) => void;
  onLeftOffsetChange: (leftOffsetPx: number | undefined) => void;
  canvasWidthPx: number;
  rowCards: CanvasCard[];
  isResizing: boolean;
  onResizingChange: (resizing: boolean) => void;
  orientation: ReportOrientation;
  readOnly?: boolean;
}

function isPresetMatch(wp: number, preset: SizePreset): boolean {
  return Math.abs(wp - PRESET_WIDTHS[preset]) < 0.02;
}

function activePreset(wp: number): SizePreset | null {
  if (isPresetMatch(wp, "S")) return "S";
  if (isPresetMatch(wp, "M")) return "M";
  if (isPresetMatch(wp, "F")) return "F";
  return null;
}

export const CardWrapper: React.FC<Props> = ({
  card,
  index,
  isSelected,
  onSelect,
  onRemove,
  onDuplicate,
  onUpdateConfig,
  onWidthChange,
  onHeightChange,
  onTopOffsetChange,
  onLeftOffsetChange,
  canvasWidthPx,
  rowCards,
  isResizing,
  onResizingChange,
  orientation,
  readOnly
}) => {
  const [hovered, setHovered] = useState(false);
  const [localResizing, setLocalResizing] = useState(false);
  const [localHeightResizing, setLocalHeightResizing] = useState(false);
  const [localPositionDragging, setLocalPositionDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHovered(true);
  };
  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHovered(false), 100);
  };

  const handleResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = card.widthPercent;
    const canvasContent = canvasWidthPx;
    const minPct = minWidthPercent(canvasContent);
    const othersSum = rowCards.filter((c) => c.id !== card.id).reduce((sum, c) => sum + c.widthPercent, 0);
    const maxPct = Math.round((100 - othersSum) * 100) / 100;

    setLocalResizing(true);
    onResizingChange(true);

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const onPointerMove = (me: PointerEvent) => {
      const deltaX = me.clientX - startX;
      const deltaPct = (deltaX / canvasContent) * 100;
      let newPct = startWidth + deltaPct;
      newPct = Math.max(minPct, Math.min(maxPct, newPct));
      newPct = Math.round(newPct * 100) / 100;
      onWidthChange(newPct, null);
    };

    const onPointerUp = () => {
      setLocalResizing(false);
      onResizingChange(false);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const handleHeightPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startHeight = cardRef.current?.getBoundingClientRect().height ?? 80;

    setLocalHeightResizing(true);
    onResizingChange(true);

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const onPointerMove = (me: PointerEvent) => {
      const newHeight = Math.max(80, startHeight + (me.clientY - startY));
      onHeightChange(Math.round(newHeight));
    };

    const onPointerUp = () => {
      setLocalHeightResizing(false);
      onResizingChange(false);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const handlePositionPointerDown = (e: React.PointerEvent) => {
    const startX = e.clientX;
    const startY = e.clientY;
    const startTop = card.topOffsetPx ?? 0;
    const startLeft = card.leftOffsetPx ?? 0;
    const xStep = Math.round(canvasWidthPx / 12);
    const yStep = 14;
    let dragging = false;
    const threshold = 5;

    // Compute workspace bounds from DOM so cards can't leave the padded area
    const cardEl = cardRef.current;
    const outerEl = cardEl?.parentElement;
    const container = outerEl?.closest("[data-rbd-droppable-id]") as HTMLElement | null;

    let minLeft = -Infinity;
    let maxLeft = Infinity;
    let minTop = 0;
    let maxTop = Infinity;

    if (cardEl && container) {
      const cardRect = cardEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const cs = window.getComputedStyle(container);
      const padT = parseFloat(cs.paddingTop) || 0;
      const padR = parseFloat(cs.paddingRight) || 0;
      const padB = parseFloat(cs.paddingBottom) || 0;
      const padL = parseFloat(cs.paddingLeft) || 0;

      // Inner edges of the workspace (content area inside padding)
      const boundLeft = containerRect.left + padL;
      const boundRight = containerRect.right - padR;
      const boundTop = containerRect.top + padT;
      const boundBottom = containerRect.bottom - padB;

      // Card edge + (newOffset - startOffset) must stay within bounds
      minLeft = startLeft + (boundLeft - cardRect.left);
      maxLeft = startLeft + (boundRight - cardRect.right);
      minTop = Math.max(0, startTop + (boundTop - cardRect.top));
      maxTop = startTop + (boundBottom - cardRect.bottom);
    }

    const snap = (value: number, step: number) => Math.round(value / step) * step;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    const onPointerMove = (me: PointerEvent) => {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;

      if (!dragging) {
        if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
        dragging = true;
        setLocalPositionDragging(true);
        onResizingChange(true);
      }

      const newTop = clamp(snap(startTop + dy, yStep), minTop, maxTop);
      const newLeft = clamp(snap(startLeft + dx, xStep), minLeft, maxLeft);
      setDragPosition({ top: newTop, left: newLeft });
    };

    const onPointerUp = (me: PointerEvent) => {
      if (dragging) {
        const finalTop = clamp(snap(startTop + (me.clientY - startY), yStep), minTop, maxTop);
        const finalLeft = clamp(snap(startLeft + (me.clientX - startX), xStep), minLeft, maxLeft);
        onTopOffsetChange(finalTop > 0 ? finalTop : undefined);
        onLeftOffsetChange(finalLeft !== 0 ? finalLeft : undefined);
        setDragPosition(null);
        setLocalPositionDragging(false);
        onResizingChange(false);
      }
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const anyResizing = localResizing || localHeightResizing || localPositionDragging;

  const currentPreset = activePreset(card.widthPercent);
  const isCustomWidth = currentPreset === null;

  const zoneColor = useMemo(() => {
    if (!localResizing || card.type !== "table" || card.config.type !== "table") return undefined;
    const cfg = card.config;
    const cols = cfg.visibleColumns || [];
    if (cols.length === 0) return undefined;
    const mw = getMinWidths(cols);
    const visible = visibleColumnCount(card.widthPercent, orientation, mw);
    const hidden = cols.length - visible;
    if (hidden === 0) return "#22C55E";
    if (hidden <= 2) return "#F59E0B";
    return "#E24B4A";
  }, [localResizing, card, orientation]);

  const cardSizeCtx = useMemo(
    () => ({
      widthPercent: card.widthPercent,
      heightPx: card.heightPx,
      topOffsetPx: card.topOffsetPx,
      leftOffsetPx: card.leftOffsetPx,
      orientation,
      isEditMode: true
    }),
    [card.widthPercent, card.heightPx, card.topOffsetPx, card.leftOffsetPx, orientation]
  );

  return (
    <Draggable draggableId={card.id} index={index} isDragDisabled={isResizing || readOnly}>
      {(provided, snapshot) => {
        const showControls = (hovered || isSelected) && !snapshot.isDragging && !readOnly;
        return (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              flex: "0 0 auto",
              width: `calc(${card.widthPercent}% - ${((rowCards.length - 1) * 14) / rowCards.length}px)`,
              minWidth: 0,
              opacity: snapshot.isDragging ? 0.35 : 1,
              zIndex: snapshot.isDragging ? 999 : anyResizing ? 50 : "auto",
              position: "relative",
              pt: showControls ? "36px" : 0,
              mt: showControls ? "-36px" : 0,
              top: dragPosition ? dragPosition.top : (card.topOffsetPx ?? 0),
              left: dragPosition ? dragPosition.left : (card.leftOffsetPx ?? 0),
              transition: anyResizing ? "none" : undefined
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            <Box
              ref={cardRef}
              onPointerDown={showControls ? handlePositionPointerDown : undefined}
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: "6px",
                boxShadow: isSelected
                  ? "0 0 0 2px #1A3C34, 0 4px 16px rgba(0,0,0,0.10)"
                  : anyResizing
                    ? "0 0 0 2px #2E6B5E, 0 4px 16px rgba(0,0,0,0.10)"
                    : "0 1px 3px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
                cursor: readOnly ? "pointer" : showControls ? "move" : snapshot.isDragging ? "grabbing" : "grab",
                position: "relative",
                ...(card.heightPx != null ? { height: card.heightPx } : { minHeight: 80 }),
                userSelect: "none",
                ...(localPositionDragging ? { opacity: 0.7 } : {})
              }}
            >
              {/* Hover toolbar */}
              {showControls && (
                <Box
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onPointerDown={(e) => e.stopPropagation()}
                  sx={{
                    position: "absolute",
                    top: -34,
                    left: 0,
                    right: 0,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "4px",
                    zIndex: 100
                  }}
                >
                  <Box
                    {...provided.dragHandleProps}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      p: "3px 8px",
                      bgcolor: "#1A3C34",
                      borderRadius: "4px",
                      cursor: "grab",
                      userSelect: "none"
                    }}
                  >
                    <GripIcon />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#FFFFFF" }}>
                      {CARD_LABELS[card.type]}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    {isCustomWidth && (
                      <>
                        <Typography component="span" sx={{ fontSize: 9, color: "#9CA3AF", mx: "1px" }}>
                          {"\u00B7"}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: 9, color: "#6B7280", fontWeight: 600 }}>
                          custom
                        </Typography>
                        <Typography component="span" sx={{ fontSize: 9, color: "#9CA3AF", mx: "1px" }}>
                          {"\u00B7"}
                        </Typography>
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const preset = card.lastPreset ?? "F";
                            onWidthChange(PRESET_WIDTHS[preset], preset);
                          }}
                          sx={{
                            border: "none",
                            bgcolor: "transparent",
                            color: "#0F5D5E",
                            fontSize: 9,
                            fontWeight: 700,
                            cursor: "pointer",
                            p: 0
                          }}
                        >
                          reset
                        </Box>
                        <Box sx={{ width: 1, height: 14, bgcolor: "#D1D5DB", mx: "1px" }} />
                      </>
                    )}
                    {(["S", "M", "F"] as SizePreset[]).map((p) => (
                      <MuiTooltip
                        key={p}
                        title={
                          card.type === "table"
                            ? p === "S"
                              ? "Small \u2014 ~3 columns"
                              : p === "M"
                                ? "Medium \u2014 ~6 columns"
                                : "Full \u2014 all columns"
                            : p === "S"
                              ? "Small (\u2153 width)"
                              : p === "M"
                                ? "Medium (\u00BD width)"
                                : "Full width"
                        }
                        arrow
                      >
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onWidthChange(PRESET_WIDTHS[p], p);
                          }}
                          sx={{
                            width: 22,
                            height: 22,
                            border: "none",
                            borderRadius: "3px",
                            bgcolor: currentPreset === p ? "#1A3C34" : "#E5E7EB",
                            color: currentPreset === p ? "#FFFFFF" : "#6B7280",
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: "pointer",
                            transition: "background 120ms ease-out"
                          }}
                        >
                          {p}
                        </Box>
                      </MuiTooltip>
                    ))}
                    {card.heightPx != null && (
                      <>
                        <Box sx={{ width: 1, height: 14, bgcolor: "#D1D5DB", mx: "1px" }} />
                        <MuiTooltip title="Reset to auto height" arrow>
                          <Box
                            component="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onHeightChange(undefined);
                            }}
                            sx={{
                              height: 22,
                              border: "none",
                              borderRadius: "3px",
                              bgcolor: "#E8F5F0",
                              color: "#0F5D5E",
                              fontSize: 9,
                              fontWeight: 700,
                              cursor: "pointer",
                              px: "5px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {"\u2195"} auto
                          </Box>
                        </MuiTooltip>
                      </>
                    )}
                    {((card.topOffsetPx ?? 0) > 0 || (card.leftOffsetPx ?? 0) !== 0) && (
                      <>
                        <Box sx={{ width: 1, height: 14, bgcolor: "#D1D5DB", mx: "1px" }} />
                        <MuiTooltip title="Reset position offset" arrow>
                          <Box
                            component="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onTopOffsetChange(undefined);
                              onLeftOffsetChange(undefined);
                            }}
                            sx={{
                              height: 22,
                              border: "none",
                              borderRadius: "3px",
                              bgcolor: "#E8F5F0",
                              color: "#0F5D5E",
                              fontSize: 9,
                              fontWeight: 700,
                              cursor: "pointer",
                              px: "5px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {"\u21BA"} pos
                          </Box>
                        </MuiTooltip>
                      </>
                    )}
                    <Box sx={{ width: 1, height: 14, bgcolor: "#D1D5DB", mx: "1px" }} />
                    <Box
                      component="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate();
                      }}
                      title="Duplicate card"
                      sx={{
                        width: 22,
                        height: 22,
                        border: "none",
                        borderRadius: "3px",
                        bgcolor: "#E8F5F0",
                        color: "#1A3C34",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {"\u29C9"}
                    </Box>
                    <Box
                      component="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                      }}
                      sx={{
                        width: 22,
                        height: 22,
                        border: "none",
                        borderRadius: "3px",
                        bgcolor: "#FEE2E2",
                        color: "#DC2626",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {"\u2715"}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* When drag handle is not shown, attach dragHandleProps to the card body.
                 For readOnly (continuation) cards, let clicks pass through so table rows
                 can still be expanded/collapsed. */}
              {!showControls && (
                <Box
                  {...provided.dragHandleProps}
                  sx={{ position: "absolute", inset: 0, ...(readOnly ? { pointerEvents: "none" } : {}) }}
                />
              )}

              <Box sx={card.heightPx != null ? { height: "100%", overflow: "hidden", borderRadius: "6px" } : undefined}>
                <CardSizeProvider value={cardSizeCtx}>
                  <CardContent card={card} onUpdateConfig={onUpdateConfig} />
                </CardSizeProvider>
              </Box>

              {/* Right-edge resize handle */}
              <Box
                onPointerDown={showControls ? handleResizePointerDown : undefined}
                title={showControls ? `Drag to resize (${Math.round(card.widthPercent)}% width)` : undefined}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: -5,
                  bottom: 4,
                  width: showControls ? 10 : 4,
                  cursor: showControls ? "col-resize" : "default",
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "3px",
                  bgcolor: localResizing
                    ? (zoneColor ?? "#1A3C34")
                    : showControls
                      ? "rgba(26,60,52,0.15)"
                      : "rgba(26,60,52,0.06)",
                  transition: "all 0.15s"
                }}
              >
                {showControls && (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{ width: 2, height: 2, borderRadius: "1px", bgcolor: localResizing ? "#fff" : "#6B7280" }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Bottom-edge resize handle */}
              <Box
                onPointerDown={showControls ? handleHeightPointerDown : undefined}
                title={
                  showControls
                    ? `Drag to resize height${card.heightPx != null ? ` (${card.heightPx}px)` : ""}`
                    : undefined
                }
                sx={{
                  position: "absolute",
                  bottom: -5,
                  left: 4,
                  right: 4,
                  height: showControls ? 10 : 4,
                  cursor: showControls ? "row-resize" : "default",
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "3px",
                  bgcolor: localHeightResizing
                    ? "#1A3C34"
                    : showControls
                      ? "rgba(26,60,52,0.15)"
                      : "rgba(26,60,52,0.06)",
                  transition: "all 0.15s"
                }}
              >
                {showControls && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 2,
                          height: 2,
                          borderRadius: "1px",
                          bgcolor: localHeightResizing ? "#fff" : "#6B7280"
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Resize width indicator */}
            {localResizing && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: -24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "#1A3C34",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  p: "2px 8px",
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  zIndex: 100,
                  pointerEvents: "none"
                }}
              >
                {Math.round(card.widthPercent)}% width
              </Box>
            )}

            {/* Resize height indicator */}
            {localHeightResizing && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: -24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "#1A3C34",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  p: "2px 8px",
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  zIndex: 100,
                  pointerEvents: "none"
                }}
              >
                {card.heightPx ?? 0}px height
              </Box>
            )}

            {/* Position drag indicator */}
            {localPositionDragging && dragPosition && (
              <Box
                sx={{
                  position: "absolute",
                  top: -24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "#1A3C34",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  p: "2px 8px",
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  zIndex: 100,
                  pointerEvents: "none"
                }}
              >
                x: {dragPosition.left}px &nbsp; y: {dragPosition.top}px
              </Box>
            )}

            {/* Ghost outline at pre-drag position */}
            {localPositionDragging && dragPosition && (
              <Box
                sx={{
                  position: "absolute",
                  // 36px accounts for toolbar padding-top on the outer box
                  top: 36 + (card.topOffsetPx ?? 0) - dragPosition.top,
                  left: (card.leftOffsetPx ?? 0) - dragPosition.left,
                  width: "100%",
                  height: cardRef.current?.getBoundingClientRect().height ?? "100%",
                  border: "2px dashed rgba(26,60,52,0.3)",
                  borderRadius: "6px",
                  pointerEvents: "none",
                  zIndex: -1
                }}
              />
            )}
          </Box>
        );
      }}
    </Draggable>
  );
};

interface CardContentProps {
  card: CanvasCard;
  onUpdateConfig: (config: CardConfig) => void;
}

const CardContent: React.FC<CardContentProps> = ({ card, onUpdateConfig }) => {
  return (
    <>
      {card.config.type === "kpi" && <KpiCard data={card.data as KPICardData | undefined} loading={card.loading} />}
      {card.config.type === "summary" && (
        <SummaryCard data={card.data as SummaryCardData | undefined} loading={card.loading} />
      )}
      {card.config.type === "chart" && <ChartCard data={card.data as Chart | undefined} loading={card.loading} />}
      {card.config.type === "table" && (
        <DataTableCard
          data={card.data as DataTableCardData | undefined}
          loading={card.loading}
          columnSettings={(card.config as TableCardConfig).columns}
        />
      )}
      {card.config.type === "text" && (
        <TextCard config={card.config} onChange={(content) => onUpdateConfig({ type: "text", content })} />
      )}
      {card.config.type === "ai_card" && (
        <AICard
          config={card.config as AICardConfig}
          data={card.data}
          loading={card.loading}
          onChange={(answer) => onUpdateConfig({ ...(card.config as AICardConfig), answer })}
        />
      )}
      {card.config.type === "exposure" && (
        <ExposureBreakdownCard
          config={card.config}
          data={card.data as ExposureCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "cashflow" && (
        <CashFlowTimelineCard data={card.data as CashFlowTimelineCardData[] | undefined} loading={card.loading} />
      )}
      {card.config.type === "unfunded" && (
        <UnfundedCommitmentsCard data={card.data as UnfundedCardData[] | undefined} loading={card.loading} />
      )}
      {card.config.type === "performance" && (
        <PerformanceAttributionCard
          config={card.config}
          data={card.data as PerformanceAttributionCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "benchmark" && (
        <BenchmarkComparisonCard
          config={card.config}
          data={card.data as BenchmarkComparisonCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "dataFreshness" && (
        <DataFreshnessCard data={card.data as DataFreshnessCardData | undefined} loading={card.loading} />
      )}
      {card.config.type === "macroBrief" && (
        <MacroBriefCard data={card.data as MacroBriefCardData | undefined} loading={card.loading} />
      )}
    </>
  );
};

const GripIcon: React.FC = () => {
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
      {[0, 1].map((col) =>
        [0, 1, 2].map((row) => (
          <circle key={`${col}-${row}`} cx={col * 5 + 2} cy={row * 4 + 2} r={1.3} fill="rgba(255,255,255,0.8)" />
        ))
      )}
    </svg>
  );
};
